import '../../App.css';
import { useState, useEffect, useMemo } from 'react';
import Seo, { toDescription } from '../../components/common/atoms/Seo';
import seoData from '../../json/seo.json';
import textData from '../../json/text.json';
import worksData from '../../json/works.json';
import Header from '../../components/menu/organisms/Header';
import Footer from  '../../components/footer/Footer';
import TopicPath from '../../components/common/atoms/TopicPath';
import H1 from '../../components/common/atoms/H1';
import BackgroundAnimation from '../../components/motion/atoms/BackgroundAnimation';
import { useParams } from 'react-router-dom';
import WorksAchievementsContent from '../../components/works/organisms/WorksAchievementsContent';
import NewsNavContainer from '../../components/common/molecules/NewsNavContainer';
import NotFound from '../NotFound';

type WorkKey = 'development' | 'logistics' | 'energy' | 'product';

/**
 * 実績ページ
 * @returns 
 */
function WorksListTemplate() {
  const works = useMemo(() => ({ works: [...worksData.works] }), []);
  const { index } = useParams();
  const achievementsIndex = Number(index);
  const isValidIndex = Number.isInteger(achievementsIndex) && achievementsIndex >= 0 && achievementsIndex < works.works.length;
  // キー名のリストを定義
  const keyNames = useMemo(() => ["development", "logistics", "energy", "product"], []);
  // 現在のインデックスに対応するエントリーを取得
  const currentWork = isValidIndex ? works.works[achievementsIndex] : undefined;
  // 存在するキーを探す
  const existingKey = currentWork ? keyNames.find(key => currentWork[key as keyof typeof currentWork]) : undefined;
  const title = currentWork && existingKey ? (currentWork[existingKey as keyof typeof currentWork] as any).title.ja : undefined;
  const workEntry = currentWork && existingKey ? (currentWork[existingKey as keyof typeof currentWork] as any) : undefined;
  
  const afterIndex = useMemo(() => isValidIndex && achievementsIndex + 1 > 0 && achievementsIndex + 1 < works.works.length ? achievementsIndex + 1 : null, [isValidIndex, achievementsIndex, works.works.length]);
  const beforeIndex = useMemo(() => isValidIndex && achievementsIndex - 1 >= 0 ? achievementsIndex - 1 : null, [isValidIndex, achievementsIndex]);

  const [beforeLinks, setBeforeLinks] = useState<linksNav | undefined>();
  const [afterLinks, setAfterLinks] = useState<linksNav | undefined>();

  // 表示実績設定
  interface linksNav {
    link: string;
    pageName: string | undefined;
  }
  const links = [
    {link:'/works', pageName:textData['menu.works']['ja']},
    {link:`/works/${existingKey}/${index}`, pageName:title}
  ];
  /**
   * 次の記事と前の記事の設定処理
   */
  useEffect(() => {
    if (beforeIndex !== null) {
      const beforeWork = works.works[beforeIndex];
      const existingBeforeKey = keyNames.find(key => beforeWork[key as WorkKey]) as WorkKey | undefined;
      let beforeTitle: string | undefined = undefined;
      if (beforeIndex !== undefined && existingBeforeKey) {
        const beforeWorkEntry = beforeWork[existingBeforeKey];
        if (beforeWorkEntry) {
          beforeTitle = beforeWorkEntry.title.ja;
        }
      }
      setBeforeLinks({
        link: `/works/${existingBeforeKey}/${beforeIndex}`,
        pageName: beforeTitle,
      });
    } else {
      setBeforeLinks(undefined);
    }

    if (afterIndex !== null) {
      const afterWork = works.works[afterIndex];
      const existingAfterKey = keyNames.find(key => afterWork[key as WorkKey]) as WorkKey | undefined;
      let afterTitle: string | undefined = undefined;
      if (afterIndex !== undefined && existingAfterKey) {
        const afterWorkEntry = afterWork[existingAfterKey];
        if (afterWorkEntry) {
          afterTitle = afterWorkEntry.title.ja;
        }
      }
      setAfterLinks({
        link: `/works/${existingAfterKey}/${afterIndex}`,
        pageName: afterTitle,
      });
    } else {
      setAfterLinks(undefined);
    }
  }, [beforeIndex, afterIndex, works.works, keyNames]);

  // 存在しない実績番号の場合は404ページを表示
  if (!isValidIndex || !workEntry) {
    return <NotFound />;
  }

  return (

    <div>
      <BackgroundAnimation />
      <Seo
        title={title}
        description={toDescription(workEntry.text.ja.join(''), seoData.pages.works.description)}
        path={`/works/${existingKey}/${index}`}
        image={workEntry.image[0]}
        type='article'
      />

      <header>
        <Header />
      </header>
      <main className="main-content">
        <TopicPath links={links} />
        <H1 text={title}/>
        <WorksAchievementsContent workEntry={workEntry} />
        <NewsNavContainer beforeLinks={beforeLinks} afterLinks={afterLinks}/>
      </main>
      <footer>
        <Footer />
      </footer>

    </div>
  );
}

export default WorksListTemplate;
